import * as React from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';

import { POSTHOG_API_KEY, SENTRY_ENVIRONMENT } from 'config/env';
import { PaddedSection, UploadAndCheckTableFiltersBar } from 'common/components';
import { generateColumns } from 'common/helpers/utils';
import { FilterData } from 'common/helpers/url';
import { PaginatedTable } from 'features/pagination/components/PaginatedTable';
import { PaginationView, AutocompleteField } from 'features/pagination/models/paginationModel';
import { Antd3Form, Antd3FormProps } from 'common/components/deprecated/antd3';
import { EditPaths } from 'features/edit/models/editModel';
import { EditContractPaths } from 'features/contract/models/contractModels';

import { CheckBenefit, CheckPaths } from '../models/checkModel';
import { CheckPendingEvidencesProps as CheckPendingEvidencesContainerProps } from '../containers/CheckPendingEvidencesContainer';
import { getCheckEvidencesBenefitsSuccess } from '../actions/checkActions';

if (SENTRY_ENVIRONMENT === 'frontend-production') {
    if (POSTHOG_API_KEY) {
        posthog.init(POSTHOG_API_KEY, {
            api_host: 'https://app.posthog.com',
            autocapture: false,
            capture_pageview: false,
        });
    }
}

const checkEvidencesTableColumns = [
    {
        title: 'Benefit',
        key: 'outcome',
        render: (outcome: string, { id, referenceNumber, contract }: CheckBenefit) => (
            <Link
                style={{
                    fontWeight: 'bold',
                }}
                to={`${CheckPaths.Evidence}${EditPaths.Contract}/${contract}/benefit/${id}`}
            >{`${outcome} | ${referenceNumber}`}</Link>
        ),
    },
    {
        title: 'Benefit Label',
        key: 'label',
        render: (label: string, { contract, id }: CheckBenefit) => (
            <Link to={`${CheckPaths.Evidence}${EditPaths.Contract}/${contract}/benefit/${id}`}>
                {label}
            </Link>
        ),
    },
    {
        title: 'Project',
        key: 'contractTitle',
        render: (contractTitle: string, { contract }: CheckBenefit) => (
            <Link to={`${EditPaths.Contract}/${contract}${EditContractPaths.ContractDetails}`}>
                {contractTitle}
            </Link>
        ),
    },
    {
        title: 'Ref. No.',
        key: 'contractReferenceNumber',
    },
];

type CheckPendingEvidencesProps = Antd3FormProps & CheckPendingEvidencesContainerProps;

class CheckPendingEvidencesForm extends React.Component<CheckPendingEvidencesProps> {
    private refreshTable = (filters?: FilterData) =>
        this.props.paginationRequest({
            view: PaginationView.CheckPendingEvidence,
            pagination: {
                current: 1,
            },
            filters,
            paginationSuccessCallback: getCheckEvidencesBenefitsSuccess,
        });

    private saveBenefitFilters = (benefitReferenceNumber: string | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.CheckPendingEvidence,
            values: {
                referenceNumber: benefitReferenceNumber,
            },
        });

    private saveContractFilters = (contractId: number | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.CheckPendingEvidence,
            values: {
                contract: contractId,
            },
        });

    private saveExecutiveOrganisationFilter = (executive: string | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.CheckPendingEvidence,
            values: { executive },
        });

    private searchByBenefits = (benefitReferenceNumber: string) =>
        this.saveBenefitFilters(benefitReferenceNumber) && this.refreshTable();

    private searchByContracts = (contractId: number) =>
        this.saveContractFilters(contractId) && this.refreshTable();

    private searchByExecutiveOrganisation = (executive: string) => {
        this.saveExecutiveOrganisationFilter(executive);
        this.refreshTable();
    };

    private onRemoveBenefitAutocomplete = () =>
        this.saveBenefitFilters(undefined) && this.refreshTable();

    private onRemoveContractAutocomplete = () =>
        this.saveContractFilters(undefined) && this.refreshTable();

    private onRemoveExecutiveOrganisationAutocomplete = () => {
        this.saveExecutiveOrganisationFilter(undefined);
        this.refreshTable();
    };

    private clearFilters = () =>
        this.props.clearFilters(PaginationView.CheckPendingEvidence) &&
        this.refreshTable() &&
        this.props.form.setFieldsValue({
            quickFilterBenefitReferenceNumber: '',
            quickFilterContractReferenceNumber: '',
        });

    public render(): JSX.Element {
        const { form, filter, checkEvidences, isTenant } = this.props;

        return (
            <PaddedSection header="Check Evidence (Pending Approval)">
                <UploadAndCheckTableFiltersBar
                    form={form}
                    searchByBenefits={this.searchByBenefits}
                    onRemoveBenefitAutocomplete={this.onRemoveBenefitAutocomplete}
                    searchByContracts={this.searchByContracts}
                    onRemoveContractAutocomplete={this.onRemoveContractAutocomplete}
                    searchByExecutiveOrganisation={this.searchByExecutiveOrganisation}
                    onRemoveExecutiveOrganisationAutocomplete={
                        this.onRemoveExecutiveOrganisationAutocomplete
                    }
                    filter={filter}
                    clearFilters={this.clearFilters}
                    benefitFilterAutocompleteField={AutocompleteField.CheckPendingEvidence}
                    isTenant={isTenant}
                />
                <PaginatedTable
                    view={PaginationView.CheckPendingEvidence}
                    paginationSuccessCallback={getCheckEvidencesBenefitsSuccess}
                    data={checkEvidences}
                    columns={generateColumns(checkEvidencesTableColumns)}
                    emptyText="No projects have been assigned to you"
                />
            </PaddedSection>
        );
    }
}

export const CheckPendingEvidences = Antd3Form.create({})(CheckPendingEvidencesForm);
